import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCatalog } from "../context/CatalogContext";
import BookCard from "../components/BookCard";

/**
 * The page for an address that does not exist.
 *
 * Every unmatched path returns the SPA shell with a 200, so without this route
 * a mistyped or dead link rendered a header, a footer and nothing in between —
 * no message, no way out. A dead end is the moment to offer the catalogue
 * rather than an apology.
 */
export default function NotFound() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { books } = useCatalog();
  const [query, setQuery] = React.useState("");

  const suggestions = React.useMemo(
    () => [...books].sort((a, b) => b.ratingCount - a.ratingCount).slice(0, 6),
    [books],
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 animate-float-up min-h-[70vh]">
      <div className="max-w-xl">
        <div className="w-12 h-12 rounded-xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center mb-5">
          <Compass className="w-6 h-6 text-brand-400" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
          There's nothing at this address
        </h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          <code className="text-xs bg-secondary px-1.5 py-0.5 rounded">{pathname}</code> doesn't
          match anything in the catalogue. The link may be old, or a letter may have gone astray.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); navigate(`/browse?q=${encodeURIComponent(query)}`); }}
          className="relative mt-7"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <label htmlFor="nf-search" className="sr-only">Search the catalogue</label>
          <input
            id="nf-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search titles, authors, narrators…"
            className="w-full h-12 pl-11 pr-4 rounded-xl bg-secondary border border-border text-sm outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
          />
        </form>

        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={() => navigate("/browse")}
            className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full px-7 h-11"
          >
            Browse everything
          </Button>
          <Button variant="outline" asChild className="rounded-full px-6 h-11 border-white/25 bg-white/5 hover:bg-white/10">
            <Link to="/">Go home</Link>
          </Button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-xl font-semibold mb-5">Or start with one of these</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-5 gap-y-8">
            {suggestions.map((b) => (
              <BookCard key={b.id} book={b} width="w-full" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
